import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskLine } from "./Reveal";
import { WaitlistForm } from "./WaitlistForm";
import { SHOTS } from "./assets";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden pt-28 md:pt-0">
      {/* atmospheric background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#080808] via-black to-black" />
      {/* gold spotlight */}
      <div className="pointer-events-none absolute -left-40 top-1/3 -z-10 h-[520px] w-[520px] rounded-full bg-[#C9A94B] opacity-[0.07] blur-[130px]" />

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-5 md:min-h-screen md:grid-cols-[1.4fr_1fr] md:px-10">
        {/* Left — copy */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-[#C9A94B]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C9A94B]">
              The Accountability Ledger · Waitlist Open
            </span>
          </motion.div>

          <h1 className="font-display text-[15vw] leading-[0.82] tracking-tight text-white md:text-[9vw] lg:text-[8.5vw]">
            <MaskLine delay={0.15}>Stop making</MaskLine>
            <MaskLine delay={0.28}>excuses.</MaskLine>
            <MaskLine delay={0.41} className="text-[#C9A94B]">Start proving it.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 max-w-md font-body text-base leading-relaxed text-[#A1A1AA] md:text-lg"
          >
            Declare what you&apos;ll do today. Then prove it with a photo or a check-in.
            No streaks without receipts. Real accountability, on the record.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-9"
          >
            <WaitlistForm testidPrefix="hero" />
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-[#5A5A5A]">
              Early access to prooftous.app · No spam · Unsubscribe anytime
            </p>
          </motion.div>
        </div>

        {/* Right — floating phone */}
        <motion.div
          style={reduce ? {} : { y: phoneY }}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto hidden w-full max-w-[300px] md:block"
        >
          <div className="pointer-events-none absolute inset-0 -z-10 scale-110 bg-[#C9A94B] opacity-[0.1] blur-[80px]" />
          <motion.div
            animate={reduce ? {} : { y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="border border-[#1F1F1F] bg-[#0A0A0A] p-2"
          >
            <img
              src={SHOTS.ledger}
              alt="proof app — today's commitment ledger"
              className="w-full"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* scroll cue */}
      <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 md:block">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5A5A5A]">Scroll</span>
      </div>
    </section>
  );
};
